import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { auditApi } from '../../api/auditApi';
import { adminApi } from '../../api/adminApi';
import {
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_LABELS,
  type AuditAction,
  type AuditEntityType,
} from '../../types/audit';
import { AuditLogTable } from '../../components/audit/AuditLogTable';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 50;

export const AdminAuditPage = () => {
  const [competitionId, setCompetitionId] = useState<number | ''>('');
  const [entityType, setEntityType] = useState<AuditEntityType | ''>('');
  const [action, setAction] = useState<AuditAction | ''>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState(0);

  const { data: competitions = [] } = useQuery({
    queryKey: ['admin', 'competitions', 'list-for-audit'],
    queryFn: () => adminApi.listCompetitions(),
  });

  const filter = useMemo(
    () => ({
      competitionId: competitionId === '' ? undefined : competitionId,
      entityType: entityType || undefined,
      action: action || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      size: PAGE_SIZE,
    }),
    [competitionId, entityType, action, from, to, page],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit', 'global', filter],
    queryFn: () => auditApi.list(filter),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const exportCsv = () => {
    if (!data) return;
    const header = ['Datum', 'Uzivatel', 'Role', 'Soutez', 'Entita', 'Akce', 'Shrnuti', 'IP'];
    const compMap = new Map(competitions.map((c) => [c.id, c.name]));
    const rows = data.items.map((e) => [
      e.changedAt,
      e.changedByUsername ?? '',
      e.changedByRole ?? '',
      e.competitionId != null ? (compMap.get(e.competitionId) ?? `#${e.competitionId}`) : '',
      AUDIT_ENTITY_LABELS[e.entityType],
      AUDIT_ACTION_LABELS[e.action],
      e.summary ?? '',
      e.ipAddress ?? '',
    ]);
    const escape = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows]
      .map((row) => row.map(escape).join(';'))
      .join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Historie změn | Admin</title>
      </Helmet>
      <PageHeader title="Historie změn" subtitle="Audit log všech akcí v jednotce" />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Soutěž</InputLabel>
              <Select
                value={competitionId}
                label="Soutěž"
                onChange={(e) => {
                  const v = e.target.value as number | '';
                  setCompetitionId(v === '' ? '' : Number(v));
                  setPage(0);
                }}
              >
                <MenuItem value="">Všechny</MenuItem>
                {competitions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Entita</InputLabel>
              <Select
                value={entityType}
                label="Entita"
                onChange={(e) => {
                  setEntityType(e.target.value as AuditEntityType | '');
                  setPage(0);
                }}
              >
                <MenuItem value="">Vše</MenuItem>
                {(Object.keys(AUDIT_ENTITY_LABELS) as AuditEntityType[]).map((t) => (
                  <MenuItem key={t} value={t}>{AUDIT_ENTITY_LABELS[t]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Akce</InputLabel>
              <Select
                value={action}
                label="Akce"
                onChange={(e) => {
                  setAction(e.target.value as AuditAction | '');
                  setPage(0);
                }}
              >
                <MenuItem value="">Vše</MenuItem>
                {(Object.keys(AUDIT_ACTION_LABELS) as AuditAction[]).map((t) => (
                  <MenuItem key={t} value={t}>{AUDIT_ACTION_LABELS[t]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="datetime-local"
              label="Od"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(0); }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="Do"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(0); }}
            />
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={exportCsv} disabled={!data || data.items.length === 0}>
              Export CSV
            </Button>
          </Stack>
          {data && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Celkem {data.total} záznamů
            </Typography>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <AuditLogTable entries={data?.items ?? []} />
          {data && data.total > PAGE_SIZE && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                color="primary"
              />
            </Stack>
          )}
        </>
      )}
    </>
  );
};
